/*
 * Skeletons for loading states. Prefer the shape of what will load:
 *   <Skeleton className="h-6 w-40" />        any block
 *   <SkeletonText lines={3} />               a paragraph
 *   <SkeletonRows rows={5} />                a table or list
 *   <SkeletonPanel />                        a whole panel
 */
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { Panel } from './Panel'

export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden="true" className={cn('block skeleton rounded-lg', className)} />
}

export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <span className={cn('flex flex-col gap-2', className)} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn('h-3.5', i === lines - 1 ? 'w-3/5' : 'w-full')} />
      ))}
    </span>
  )
}

export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }) {
  const { t } = useTranslation('common')
  return (
    <div className={cn('flex flex-col', className)} role="status" aria-label={t('ui.loading')}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 border-b border-hairline px-5 py-4 last:border-0">
          <Skeleton className="size-9 rounded-xl" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="hidden h-6 w-20 rounded-full sm:block" />
          <Skeleton className="hidden h-3.5 w-24 md:block" />
        </div>
      ))}
    </div>
  )
}

export function SkeletonPanel({ className, lines = 3 }: { className?: string; lines?: number }) {
  const { t } = useTranslation('common')
  return (
    <Panel padded className={className} role="status" aria-label={t('ui.loading')}>
      <Skeleton className="h-4 w-1/3" />
      <SkeletonText lines={lines} className="mt-4" />
    </Panel>
  )
}
